/**
 * InstallAppPrompt — Banner flutuante "📱 Instalar app" para PWA.
 *
 * Comportamento:
 *  - Em Android Chrome: captura o evento `beforeinstallprompt` e mostra
 *    botão que dispara o prompt nativo do navegador.
 *  - Em iOS Safari: mostra instruções manuais ("Compartilhar → Adicionar
 *    à Tela de Início") pois iOS não tem prompt automático.
 *  - Esconde se o app já está instalado (`display-mode: standalone`).
 *  - Esconde se o usuário descartou nesta sessão (sessionStorage).
 */
import React, { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "gofood_install_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iPhone|iPad|iPod/.test(navigator.userAgent || "");
}

export default function InstallAppPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // já instalado
    if (localStorage.getItem(DISMISS_KEY)) return; // usuário fechou
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Pequeno delay pra não atrapalhar o load inicial
      setTimeout(() => setVisible(true), 4500);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS — não dispara o evento. Mostra ajuda manual após 6s.
    if (isIOS()) {
      const t = setTimeout(() => setVisible(true), 6500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", handler);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismiss = () => {
    setVisible(false);
    setShowIosHelp(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") {
        dismiss();
      } else {
        setVisible(false);
      }
      setDeferred(null);
    } else if (isIOS()) {
      setShowIosHelp(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed left-3 right-3 bottom-[88px] z-[60] bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl shadow-2xl flex items-center gap-3 px-4 py-3 animate-in slide-in-from-bottom"
        data-testid="install-app-prompt"
        role="dialog"
      >
        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
          <Download className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-extrabold">Instale o app GoFood</div>
          <div className="text-[11px] text-white/80">Acesso rápido, notificações e modo offline</div>
        </div>
        <button
          type="button"
          onClick={install}
          className="bg-white text-orange-600 px-3 py-1.5 rounded-full text-xs font-extrabold whitespace-nowrap hover:bg-orange-50"
          data-testid="install-app-btn"
        >
          Instalar
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Fechar"
          className="ml-1 text-white/70 hover:text-white"
          data-testid="install-app-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {showIosHelp && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4" onClick={dismiss}>
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-bold mb-2 text-gray-900">Instalar no iPhone</h3>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="font-bold">1.</span>
                <span>Toque no botão <Share className="inline w-4 h-4 mx-0.5 text-blue-600" /> <strong>Compartilhar</strong> abaixo da tela</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">2.</span>
                <span>Role pra baixo e toque em <Plus className="inline w-4 h-4 mx-0.5" /> <strong>Adicionar à Tela de Início</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">3.</span>
                <span>Toque em <strong>Adicionar</strong> no canto superior direito</span>
              </li>
            </ol>
            <button
              onClick={dismiss}
              className="mt-4 w-full py-2.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
}
